/**
 * AI ops (ADR-054/058/064/065) — the internal control room for the AI layer.
 *
 * Five tabs: Agents (the switches — every agent ships disabled), Runs (the
 * full ai_runs audit), Alerts (what the risk monitor raised; ack is history,
 * not deletion), Proposals (suggestions an operator must decide — deciding
 * moves NO money), and Digest (the executive digest, prose beside the exact
 * JSON it was written from).
 *
 * The whole `/internal/admin/ai/*` surface exists only when `AI_OPS_ENABLED`
 * is set server-side; with the layer off these routes 404 and this screen says
 * so honestly instead of erroring. Rule 7 note: this page is INTERNAL — none
 * of it is ever referenced by client code or player-facing copy.
 */
import { useState, type ReactNode } from 'react';
import {
  ackAiAlert,
  aiOff,
  decideAiProposal,
  fetchAiAgents,
  fetchAiAlerts,
  fetchAiDigests,
  fetchAiProposals,
  fetchAiRuns,
  setAiAgentEnabled,
  type AiAgent,
  type AiDigestView,
  type AiProposalView,
  type AiRunView,
  type OpsAlertView,
} from '../api/aiops.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute } from '../lib/datetime.ts';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { Badge, Card, EmptyState, ErrorState, TableSkeleton } from '../ui/primitives.tsx';
import { IconPulse, IconShield, IconSliders } from '../ui/icons.tsx';

type Maybe<T> = { off: true } | { off: false; data: T };

async function tryLoad<T>(fn: () => Promise<T>): Promise<Maybe<T>> {
  try {
    return { off: false, data: await fn() };
  } catch (cause) {
    if (aiOff(cause)) return { off: true };
    throw cause;
  }
}

type TabId = 'agents' | 'runs' | 'alerts' | 'proposals' | 'digest';

const TABS: { id: TabId; label: string }[] = [
  { id: 'agents', label: 'Agents' },
  { id: 'runs', label: 'Runs' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'proposals', label: 'Proposals' },
  { id: 'digest', label: 'Digest' },
];

function LayerOff(): ReactNode {
  return (
    <EmptyState
      compact
      icon={<IconShield size="lg" />}
      title="The AI layer is off"
      hint="AI_OPS_ENABLED is not set on the server, so nothing is registered — no agent runs, no route exists, no cost accrues. Turning it on is an owner decision (an env var plus the per-agent switches here)."
    />
  );
}

export function AiOps(): ReactNode {
  const [tab, setTab] = useState<TabId>('agents');

  return (
    <div className="stack">
      <Card
        title="AI ops"
        icon={<IconPulse />}
        sub="Internal and advisory only — heuristics decide, models narrate; nothing here moves money or touches an outcome"
      >
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'agents' && <AgentsTab />}
        {tab === 'runs' && <RunsTab />}
        {tab === 'alerts' && <AlertsTab />}
        {tab === 'proposals' && <ProposalsTab />}
        {tab === 'digest' && <DigestTab />}
      </Card>
    </div>
  );
}

// -------------------------------------------------------------------- agents

function AgentsTab(): ReactNode {
  const agents = useAsync(() => tryLoad(fetchAiAgents), []);
  const [busy, setBusy] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  if (agents.error) return <ErrorState title="Agents did not load" detail={agents.error} onRetry={agents.reload} />;
  if (agents.loading || !agents.data) return <TableSkeleton columns={['text', 'text', 'num']} rows={4} />;
  if (agents.data.off) return <LayerOff />;
  const rows: AiAgent[] = agents.data.data;

  async function toggle(agent: AiAgent): Promise<void> {
    const enabling = !agent.enabled;
    const result = await confirm({
      title: enabling ? `Enable ${agent.agent}` : `Disable ${agent.agent}`,
      intent: enabling ? 'default' : 'danger',
      body: enabling
        ? 'The agent starts acting on its next tick, inside its daily USD budget. Every run is audited. No agent moves money or touches a game outcome.'
        : 'The agent stops on its next tick. Its history stays.',
      confirmLabel: enabling ? 'Enable' : 'Disable',
      facts: [
        { label: 'Agent', value: agent.agent },
        { label: 'Model', value: agent.model },
        { label: 'Daily budget', value: `$${agent.dailyBudgetUsd}` },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(agent.agent);
    try {
      await setAiAgentEnabled(agent.agent, enabling);
      toast(`${agent.agent} is now ${enabling ? 'enabled' : 'disabled'}.`);
      agents.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not toggle the agent.', 'error');
    } finally {
      setBusy(null);
    }
  }

  if (rows.length === 0) return <p className="field__hint">No agents have config rows yet.</p>;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Agent</th>
          <th>Model</th>
          <th className="num">Daily budget</th>
          <th>State</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.agent}>
            <td>{a.agent}</td>
            <td className="mono-code">{a.model}</td>
            <td className="num">${a.dailyBudgetUsd}</td>
            <td>
              <Badge tone={a.enabled ? 'win' : 'neutral'}>{a.enabled ? 'enabled' : 'disabled'}</Badge>
            </td>
            <td>
              <button
                type="button"
                className={`btn btn--sm ${a.enabled ? 'btn--danger' : 'btn--primary'}`}
                disabled={busy !== null}
                onClick={() => void toggle(a)}
              >
                {busy === a.agent ? 'Working…' : a.enabled ? 'Disable' : 'Enable'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------- runs

const RUN_TONE: Record<AiRunView['status'], 'win' | 'loss' | 'pending' | 'info'> = {
  ok: 'win',
  error: 'loss',
  refused: 'pending',
  budget_denied: 'info',
};

function RunsTab(): ReactNode {
  const [agent, setAgent] = useState('');
  const runs = useAsync(
    () => tryLoad(() => fetchAiRuns(agent === '' ? {} : { agent })),
    [agent],
  );
  const [open, setOpen] = useState<number | null>(null);

  if (runs.error) return <ErrorState title="Runs did not load" detail={runs.error} onRetry={runs.reload} />;
  if (runs.loading || !runs.data) return <TableSkeleton columns={['text', 'text', 'num', 'num']} rows={6} />;
  if (runs.data.off) return <LayerOff />;
  const rows: AiRunView[] = runs.data.data.runs;

  return (
    <>
      <div className="toolbar">
        <input
          className="input input--sm"
          placeholder="Filter by agent…"
          value={agent}
          onChange={(e) => setAgent(e.target.value.trim())}
        />
      </div>
      {rows.length === 0 ? (
        <p className="field__hint">No runs recorded{agent ? ` for ${agent}` : ''}.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Agent</th>
              <th>Status</th>
              <th className="num">Cost</th>
              <th className="num">Latency</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <>
                <tr key={r.id}>
                  <td className="num">{absolute(r.createdAt)}</td>
                  <td>{r.agent}</td>
                  <td><Badge tone={RUN_TONE[r.status]}>{r.status}</Badge></td>
                  <td className="num">{r.costUsd === null ? '—' : `$${r.costUsd}`}</td>
                  <td className="num">{r.latencyMs === null ? '—' : `${r.latencyMs} ms`}</td>
                  <td>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setOpen(open === r.id ? null : r.id)}>
                      {open === r.id ? 'Hide' : 'Detail'}
                    </button>
                  </td>
                </tr>
                {open === r.id ? (
                  <tr key={`${r.id}-detail`}>
                    <td colSpan={6}>
                      <pre className="mono-code" style={undefined}>
                        {`prompt:\n${r.prompt}\n\noutput:\n${r.output ?? '(none)'}`}
                      </pre>
                    </td>
                  </tr>
                ) : null}
              </>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// -------------------------------------------------------------------- alerts

function AlertsTab(): ReactNode {
  const [status, setStatus] = useState<'open' | 'acked'>('open');
  const alerts = useAsync(() => tryLoad(() => fetchAiAlerts(status)), [status]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  if (alerts.error) return <ErrorState title="Alerts did not load" detail={alerts.error} onRetry={alerts.reload} />;
  if (alerts.loading || !alerts.data) return <TableSkeleton columns={['text', 'text', 'num']} rows={4} />;
  if (alerts.data.off) return <LayerOff />;
  const rows: OpsAlertView[] = alerts.data.data;

  async function ack(id: number): Promise<void> {
    setBusy(true);
    try {
      await ackAiAlert(id);
      toast('Alert acknowledged — it stays in the acked history.');
      alerts.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not ack the alert.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Alert status">
          {(['open', 'acked'] as const).map((s) => (
            <button key={s} type="button" className={`segment${status === s ? ' is-active' : ''}`} onClick={() => setStatus(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="field__hint">No {status} alerts.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th>Scope</th>
              <th>Detail</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="num">{absolute(a.createdAt)}</td>
                <td><Badge tone="pending">{a.kind}</Badge></td>
                <td>{a.scope ?? '—'}{a.refId !== null ? ` #${a.refId}` : ''}</td>
                <td className="field__hint">{a.detail ? JSON.stringify(a.detail).slice(0, 160) : '—'}</td>
                <td>
                  {a.status === 'open' ? (
                    <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void ack(a.id)}>
                      Ack
                    </button>
                  ) : (
                    <Badge tone="neutral">acked</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// ----------------------------------------------------------------- proposals

function ProposalsTab(): ReactNode {
  const [status, setStatus] = useState<'open' | 'armed' | 'dismissed'>('open');
  const proposals = useAsync(() => tryLoad(() => fetchAiProposals(status)), [status]);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  if (proposals.error) return <ErrorState title="Proposals did not load" detail={proposals.error} onRetry={proposals.reload} />;
  if (proposals.loading || !proposals.data) return <TableSkeleton columns={['text', 'text', 'num']} rows={4} />;
  if (proposals.data.off) return <LayerOff />;
  const rows: AiProposalView[] = proposals.data.data;

  async function decide(p: AiProposalView, verdict: 'armed' | 'dismissed'): Promise<void> {
    const result = await confirm({
      title: verdict === 'armed' ? 'Mark as armed' : 'Dismiss this proposal',
      body:
        verdict === 'armed'
          ? 'Armed records that YOU acted on this through the real tools (promo grants keep their own gates and caps). Deciding a proposal moves no money.'
          : 'Dismissed records that you chose not to act. The proposal stays in history.',
      confirmLabel: verdict === 'armed' ? 'Mark armed' : 'Dismiss',
      facts: [
        { label: 'Agent', value: p.agent },
        { label: 'Kind', value: p.kind },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await decideAiProposal(p.id, verdict);
      toast(`Proposal ${verdict}.`);
      proposals.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not decide the proposal.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Proposal status">
          {(['open', 'armed', 'dismissed'] as const).map((s) => (
            <button key={s} type="button" className={`segment${status === s ? ' is-active' : ''}`} onClick={() => setStatus(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="field__hint">No {status} proposals.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Agent</th>
              <th>Kind</th>
              <th>Detail</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="num">{absolute(p.createdAt)}</td>
                <td>{p.agent}</td>
                <td><Badge tone="info">{p.kind}</Badge></td>
                <td className="field__hint">{p.detail ? JSON.stringify(p.detail).slice(0, 160) : '—'}</td>
                <td>
                  {p.status === 'open' ? (
                    <span className="row-actions">
                      <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => void decide(p, 'armed')}>
                        Armed
                      </button>
                      <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void decide(p, 'dismissed')}>
                        Dismiss
                      </button>
                    </span>
                  ) : (
                    <Badge tone={p.status === 'armed' ? 'win' : 'neutral'}>{p.status}</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// -------------------------------------------------------------------- digest

function DigestTab(): ReactNode {
  const digests = useAsync(() => tryLoad(() => fetchAiDigests()), []);
  const [showJson, setShowJson] = useState<number | null>(null);

  if (digests.error) return <ErrorState title="Digests did not load" detail={digests.error} onRetry={digests.reload} />;
  if (digests.loading || !digests.data) return <TableSkeleton columns={['text', 'text']} rows={3} />;
  if (digests.data.off) return <LayerOff />;
  const rows: AiDigestView[] = digests.data.data;

  if (rows.length === 0) {
    return (
      <EmptyState
        compact
        icon={<IconSliders size="lg" />}
        title="No digests yet"
        hint="The pipeline writes one per period once the exec_digest agent is enabled. Every figure is SQL-computed; the model writes only the prose."
      />
    );
  }

  return (
    <div className="stack">
      {rows.map((d) => (
        <div className="subcard" key={d.id}>
          <p className="field__hint">
            <Badge tone="info">{d.kind}</Badge> {d.periodStart} · written {absolute(d.createdAt)}
          </p>
          {d.body.trim() === '' ? (
            <p className="field__hint">
              (Prose skipped — keyless, budget, or the no-invented-numbers guard. The figures below
              still arrived.)
            </p>
          ) : (
            <p style={undefined}>{d.body}</p>
          )}
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => setShowJson(showJson === d.id ? null : d.id)}
          >
            {showJson === d.id ? 'Hide figures' : 'Show the exact figures the prose was written from'}
          </button>
          {showJson === d.id ? (
            <pre className="mono-code">{JSON.stringify(d.inputJson, null, 2)}</pre>
          ) : null}
        </div>
      ))}
    </div>
  );
}
